import { RegistrationFeesType } from '../../../../../../models/event-fees/event-fees.interface'
import { IRegistrationByDayInterface } from '../../../../../../models/registrations-by-day/registrationByDay.interface'
import { IScoreCardInterface } from '../../../../../../models/score-cards/score-cards.interface'

/**
 * Parses a US address string into structured components.
 *
 * @param address - A string containing a US address in the format:
 *                 "street, city, ST 12345" or "street, city, ST 12345, USA"
 * @returns An object with parsed address components (street, city, state, zip)
 *          or null if the address doesn't match the expected format
 *
 * @example
 * parseAddress("123 Main St, Springfield, IL 62701")
 *   Returns: {
 *     street: "123 Main St",
 *     city: "Springfield",
 *     state: "IL",
 *     zip: "62701"
 *   }
 *
 * @example
 * parseAddress("Invalid Address")
 *   Returns: null
 */
export function parseAddress(address: string) {
  const addressRegex = /^(.*),\s*(.*),\s*([A-Z]{2})\s*(\d{5})(?:,\s*USA)?$/
  const matches = address.match(addressRegex)

  if (matches) {
    return {
      street: matches[1],
      city: matches[2],
      state: matches[3],
      zip: matches[4],
    }
  } else {
    return null // Address format does not match the expected pattern
  }
}

/**
 * Creates a mapping between class names and their associated score sheets.
 *
 * Not all registrationsByDay objects have a score.sheet, but score sheets are
 * assigned at the class level. This function creates a reliable mapping of
 * classes to score sheets and score sheet types instead of relying on data
 * in the registrationsByDay objects for each registration.
 *
 * @param registrationFees - Array of registration fee objects used to extract unique class names
 * @param registrationsByDay - Array of registration by day objects containing score sheet information
 * @returns An object mapping class names to their score sheet name and type
 *
 * @example
 * Given:
 * registrationFees = [{ name: 'Some Class' }]
 * registrationsByDay = [{
 *   registrationByDayName: 'Some Class',
 *   score: { sheet: { name: 'Standard Score Sheet', type: 'hunter' } }
 * }]
 *
 * Returns:
 * { 'Some Class': { scoreSheetName: 'Standard Score Sheet', scoreSheetType: 'hunter' } }
 */
export function createClassToScoreSheetMapping(
  registrationFees: RegistrationFeesType[],
  registrationsByDay: IRegistrationByDayInterface[]
) {
  // get all unique class names from registrationFees
  const uniqueClassNames = registrationFees
    .map((fee: RegistrationFeesType) => fee.name)
    .filter(Boolean)

  // mapping object
  const classToScoreSheetMap: Record<
    string,
    { scoreSheetName: string; scoreSheetType: string | null }
  > = {}

  // loop through registrationsByDay to find score sheets for each class
  registrationsByDay.forEach((registration: IRegistrationByDayInterface) => {
    const className = registration.registrationByDayName

    // skip if className null / undefined
    if (!className) {
      return
    }

    // skip if this class isn't in our list or we already found a mapping for it
    if (!uniqueClassNames.includes(className) || classToScoreSheetMap[className]) {
      return
    }

    // check if this registration has a defined score sheet
    if (registration.score?.sheet?.name && registration.score.sheet.name !== '') {
      // map the class name to the score sheet name
      classToScoreSheetMap[className] = {
        scoreSheetName: registration.score.sheet.name,
        scoreSheetType: registration.score.sheet.type,
      }
    }
  })

  return classToScoreSheetMap
}

/**
 * Converts a score card name to its corresponding fence type code.
 * Used in USEF export functionality.
 *
 * @param scoreCardName - The name of the score card to convert
 * @returns A single character code representing the fence type ('E' for Equitation, 'H' for Hunters, or empty string for all score cards that do not need special formatting in the export)
 */
export const getFenceTypeByScoreCardName = (scoreCardName: string) => {
  switch (scoreCardName) {
    case 'Equitation':
      return 'E'
    case 'Hunters':
      return 'H'
    default:
      return ''
  }
}

/**
 * Creates a mapping between class names and their associated fence types.
 * This mapping is derived from each registration fee's defined score card.
 *
 * @param registrationFees - Array of registration fee objects
 * @returns An object where keys are class names and values contain scoreCard name and
 *          fence type code (derived from the scoreCard)
 *
 * @example
 * Given registration fees:
 * [{ name: 'Some Class', scoreCard: 'Equitation' }]
 * Returns:
 * { 'Some Class': { scoreCard: 'Equitation', fenceType: 'E' } }
 */
export const createFenceTypeClassMapping = (registrationFees: RegistrationFeesType[]) => {
  const classToFenceTypeMap: Record<string, { scoreCard: string; fenceType: string }> = {}

  if (!registrationFees || !Array.isArray(registrationFees)) {
    return classToFenceTypeMap // return empty mapping if registrationFees is invalid
  }

  registrationFees.forEach((fee: RegistrationFeesType) => {
    if (!fee.name) return

    classToFenceTypeMap[fee.name] = {
      scoreCard: fee.scoreCard || '',
      fenceType: getFenceTypeByScoreCardName(fee.scoreCard || ''),
    }
  })
  return classToFenceTypeMap
}

/**
 * Finds the fence height raw value associated with a given score card name.
 *
 * @param scoreCardName - The name of the score card to search for
 * @param hunterJumperScoreCardsArr - Array of score card objects to search through
 * @returns The fence height string if found, or an empty string if not found or if no scoreCardName is provided
 *
 * @example
 * findFenceHeightByScoreCard('Novice Hunter', scoreCards)
 *   Returns: '2'6"' (if that height exists in the matching score card)
 *
 * @example
 * findFenceHeightByScoreCard('', scoreCards)
 *   Returns: ''
 */
export const findFenceHeightByScoreCard = (
  scoreCardName: string,
  hunterJumperScoreCardsArr: IScoreCardInterface[]
) => {
  if (!scoreCardName) return ''

  const scoreCardMetadata = hunterJumperScoreCardsArr?.find((card) => {
    return card.name === scoreCardName
  })

  return scoreCardMetadata?.fenceHeight || ''
}

/**
 * Converts various fence height formats to a standardized display format.
 *
 * This function handles multiple fence height formats:
 * - Named designations (N - National, H - High Performance, etc.)
 * - Imperial measurements (e.g., 3'3")
 * - Metric measurements (e.g., 1.55m)
 *
 * For imperial measurements, it converts to total inches.
 * For metric measurements, it extracts just the numeric value.
 *
 * @param rawFenceHeight - The fence height string to convert
 * @returns A standardized display format string:
 *          - Single letter for named designations (N, H, A, R)
 *          - "0" for "Poles"
 *          - Total inches for imperial measurements
 *          - Numeric value only for metric measurements
 *          - Original string if format is unrecognized
 *
 * @example
 * convertFenceHeightToDisplay('N - National')
 *   Returns: 'N'
 *
 * @example
 * convertFenceHeightToDisplay('3\'6"')
 *   Returns: '42'
 *
 * @example
 * convertFenceHeightToDisplay('1.20m')
 *   Returns: '1.20'
 */
export const convertFenceHeightToDisplay = (rawFenceHeight: string) => {
  if (!rawFenceHeight) return ''

  switch (rawFenceHeight) {
    case 'N - National':
      return 'N'
    case 'H - High Performance':
      return 'H'
    case 'A - American':
      return 'A'
    case 'R - Regional':
      return 'R'
    case 'Poles':
      return '0'
    default:
      // check if rawFenceHeight is imperial (e.g., 3'3")
      if (rawFenceHeight.includes('’')) {
        // extract feet and inches
        const imperialRegex = /(\d+)’(\d+)”/
        const match = rawFenceHeight.match(imperialRegex)

        if (match) {
          const feet = parseInt(match[1], 10)
          const inches = parseInt(match[2], 10)

          // convert to total inches
          const totalInches = feet * 12 + inches

          return totalInches.toString()
        }
      }

      // Check if rawFenceHeight is metric (e.g., 1.55m)
      if (rawFenceHeight.includes('m')) {
        // extract the number part
        const metricRegex = /(\d+\.?\d*)/
        const match = rawFenceHeight.match(metricRegex)

        if (match) {
          return match[1] // return just the number part
        }
      }

      //fallback if can't parse - return raw
      return rawFenceHeight
  }
}
